import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Bold, Underline as UnderlineIcon, Strikethrough, List, ListOrdered, AlignLeft, AlignCenter,
  AlignRight, AlignJustify, Link2, Eraser, Code2, UploadCloud, Loader2, Save, Search, Code, Users,
  GraduationCap, CalendarDays, Building2, Wand2, ChevronDown, Table2, Image as ImageIcon,
  Indent, Outdent, Baseline, Highlighter, Type, Link as LinkIcon, Wallet, Eye,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { graduation: GraduationCap, users: Users, building: Building2, calendar: CalendarDays, wallet: Wallet };

const STYLES = [
  { label: "Normal", block: "P", className: "text-sm" },
  { label: "Heading 1", block: "H1", className: "text-2xl font-bold" },
  { label: "Heading 2", block: "H2", className: "text-xl font-bold" },
  { label: "Heading 3", block: "H3", className: "text-lg font-bold" },
  { label: "Heading 4", block: "H4", className: "text-base font-semibold" },
  { label: "Quote", block: "BLOCKQUOTE", className: "text-sm italic text-slate-500" },
];

const FONTS = [
  "Arial", "Helvetica", "Segoe UI", "Calibri", "Verdana", "Tahoma", "Trebuchet MS",
  "Times New Roman", "Georgia", "Garamond", "Cambria", "Book Antiqua", "Palatino Linotype",
  "Century Gothic", "Franklin Gothic", "Lucida Sans", "Courier New", "Roboto", "Open Sans", "Montserrat",
  "Playfair Display", "Cinzel", "EB Garamond", "Cormorant Garamond", "Merriweather", "Libre Baskerville",
  "Lora", "PT Serif", "Great Vibes", "Pinyon Script", "Tangerine", "Dancing Script", "Parisienne", "Allura",
  "Poppins", "Raleway", "Oswald",
];

const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

const SWATCHES = ["#000000", "#434343", "#666666", "#999999", "#cccccc", "#ffffff",
  "#e11d48", "#f97316", "#eab308", "#22c55e", "#0ea5e9", "#6366f1", "#a855f7", "#ec4899"];

const ALIGN_TOOLS = [
  { cmd: "justifyLeft", icon: AlignLeft, title: "Align left" },
  { cmd: "justifyCenter", icon: AlignCenter, title: "Align center" },
  { cmd: "justifyRight", icon: AlignRight, title: "Align right" },
  { cmd: "justifyFull", icon: AlignJustify, title: "Justify" },
  { cmd: "outdent", icon: Outdent, title: "Decrease indent" },
  { cmd: "indent", icon: Indent, title: "Increase indent" },
];

const TBtn = ({ title, onClick, active, children, testid }) => (
  <button type="button" title={title} data-testid={testid}
    onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`h-8 min-w-8 px-1.5 rounded flex items-center justify-center hover:bg-slate-200 ${active ? "bg-slate-200 text-indigo-600" : "text-slate-600"}`}>
    {children}
  </button>
);

export default function TemplateEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const editorRef = useRef(null);
  const savedRange = useRef(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [content, setContent] = useState("");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [showHtml, setShowHtml] = useState(false);
  const [grid, setGrid] = useState({ r: 0, c: 0 });
  const [imgUrl, setImgUrl] = useState("");
  const [groups, setGroups] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [selStudent, setSelStudent] = useState("sample");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    api.get("/templates/placeholders").then((r) => setGroups(r.data.groups || []))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail)));
    api.get("/templates/sample-students").then((r) => setStudents(r.data.students || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/templates/${id}`).then((r) => {
      setName(r.data.name || ""); setCategory(r.data.category || ""); setThumbnail(r.data.thumbnail || "");
      setContent(r.data.content || "");
      if (editorRef.current) editorRef.current.innerHTML = r.data.content || "";
    }).catch((e) => { toast.error(apiErr(e.response?.data?.detail)); nav("/templates"); })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const snapshot = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0);
    }
  };
  const restore = () => {
    const sel = window.getSelection();
    if (savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
  };
  const sync = () => { setContent(editorRef.current?.innerHTML || ""); snapshot(); };

  const run = (cmd, val = null) => {
    editorRef.current?.focus();
    restore();
    document.execCommand(cmd, false, val);
    sync();
  };
  const insertHTML = (html) => {
    editorRef.current?.focus();
    restore();
    document.execCommand("insertHTML", false, html);
    sync();
  };

  const setBlock = (block) => run("formatBlock", block);
  const setFont = (font) => run("fontName", font);
  const setColor = (c) => run("foreColor", c);
  const setHighlight = (c) => { if (!document.execCommand("hiliteColor", false, c)) run("backColor", c); else sync(); };

  const setSize = (px) => {
    editorRef.current?.focus();
    restore();
    document.execCommand("fontSize", false, "7");
    editorRef.current?.querySelectorAll('font[size="7"]').forEach((f) => {
      f.removeAttribute("size"); f.style.fontSize = px + "px";
    });
    sync();
  };

  const insertLink = () => {
    const url = window.prompt("Link URL");
    if (!url) return;
    run("createLink", url);
  };

  const insertTable = (rows, cols) => {
    let html = '<table style="border-collapse:collapse;width:100%" border="1">';
    for (let i = 0; i < rows; i++) {
      html += "<tr>";
      for (let j = 0; j < cols; j++) html += '<td style="border:1px solid #cbd5e1;padding:6px;min-width:40px">&nbsp;</td>';
      html += "</tr>";
    }
    html += "</table><p><br/></p>";
    insertHTML(html);
    setGrid({ r: 0, c: 0 });
  };

  const _selCells = () => {
    const s = window.getSelection();
    if (!s || !s.rangeCount) return { table: null, cells: [] };
    let n = s.getRangeAt(0).commonAncestorContainer;
    while (n && n.nodeType === 3) n = n.parentNode;
    const table = n && n.closest ? n.closest("table") : null;
    if (!table || !editorRef.current?.contains(table)) return { table: null, cells: [] };
    const range = s.getRangeAt(0);
    const cells = [...table.querySelectorAll("td,th")].filter((c) => range.intersectsNode(c));
    return { table, cells };
  };
  const toggleTableBorders = () => {
    editorRef.current?.focus(); restore();
    const { table } = _selCells();
    const tables = table ? [table] : [...(editorRef.current?.querySelectorAll("table") || [])];
    if (!tables.length) { toast.error("Place cursor inside a table first"); return; }
    tables.forEach((tb) => {
      const off = tb.getAttribute("data-noborder") === "1";
      tb.setAttribute("data-noborder", off ? "0" : "1");
      tb.style.border = off ? "1px solid #cbd5e1" : "none";
      tb.querySelectorAll("td,th").forEach((c) => { c.style.border = off ? "1px solid #cbd5e1" : "none"; });
    });
    sync();
  };
  const mergeCells = () => {
    editorRef.current?.focus(); restore();
    const { cells } = _selCells();
    if (cells.length < 2) { toast.error("Select 2 or more cells to merge"); return; }
    const first = cells[0];
    const sameRow = cells.every((c) => c.parentNode === first.parentNode);
    if (sameRow) {
      let span = 0; cells.forEach((c) => { span += c.colSpan || 1; });
      cells.slice(1).forEach((c) => { if (c.innerHTML.trim() && c.innerHTML !== "&nbsp;") first.innerHTML += " " + c.innerHTML; c.remove(); });
      first.colSpan = span;
    } else if (cells.every((c) => c.cellIndex === first.cellIndex)) {
      let span = 0; cells.forEach((c) => { span += c.rowSpan || 1; });
      cells.slice(1).forEach((c) => { if (c.innerHTML.trim() && c.innerHTML !== "&nbsp;") first.innerHTML += " " + c.innerHTML; c.remove(); });
      first.rowSpan = span;
    } else { toast.error("Select cells within a single row or a single column"); return; }
    sync();
  };

  const insertImageUrl = () => {
    if (!imgUrl.trim()) return;
    insertHTML(`<img src="${imgUrl.trim()}" style="max-width:100%" />`);
    setImgUrl("");
  };
  const insertImageFile = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => insertHTML(`<img src="${reader.result}" style="max-width:100%" />`);
    reader.readAsDataURL(file);
  };

  const insertTag = (tag) => insertHTML(`[${tag}]`);
  const onEditorInput = () => snapshot();

  const onThumb = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setThumbnail(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Enter template name"); return; }
    setSaving(true);
    try {
      const html = editorRef.current?.innerHTML ?? content;
      const payload = { name: name.trim(), category: category.trim() || "General", thumbnail, content: html };
      if (id) { await api.put(`/templates/${id}`, payload); toast.success("Template updated"); }
      else { await api.post("/templates", payload); toast.success("Template created"); }
      nav("/templates");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runPreview = async (sid) => {
    setPreviewLoading(true);
    try {
      const body = { content: editorRef.current?.innerHTML || content };
      if (sid && sid !== "sample") body.student_id = sid;
      const r = await api.post("/templates/render", body);
      setPreviewHtml(r.data.html || "");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setPreviewLoading(false); }
  };
  const openPreview = async () => { setPreviewOpen(true); await runPreview(selStudent); };

  const filteredGroups = groups.map((g) => ({
    ...g, tags: g.tags.filter((t) => !q.trim() || t.includes(q.trim().toLowerCase())),
  })).filter((g) => g.tags.length);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-5" data-testid="template-editor-page">
      <h1 className="font-heading text-3xl font-extrabold text-slate-900">{id ? "Edit Template" : "Add New Template"}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 border-t-4 border-t-indigo-500 p-6 space-y-5">
          <h2 className="font-heading font-bold text-slate-900">Create New Template</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Template Name <span className="text-rose-500">*</span></Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter template name (e.g., Monthly Report)" className="mt-1" data-testid="te-name" /></div>
            <div><Label>Category <span className="text-rose-500">*</span></Label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Enter category (e.g., Academic, General)" className="mt-1" data-testid="te-category" /></div>
          </div>

          <div>
            <Label>Thumbnail Preview Image <span className="text-slate-400 font-normal">(Optional)</span></Label>
            <div className="flex items-start gap-4 mt-1">
              <label className="w-28 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-400 cursor-pointer hover:border-indigo-400 overflow-hidden shrink-0" data-testid="te-thumb-drop">
                {thumbnail ? <img src={thumbnail} alt="thumb" className="w-full h-full object-cover" /> : <><UploadCloud size={22} /><span className="text-xs">Click to upload</span></>}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onThumb(e.target.files?.[0])} data-testid="te-thumb-input" />
              </label>
              <div className="text-sm text-slate-500">
                <p>Upload a screenshot of this template for faster gallery loading across all schools.</p>
                <p className="text-xs text-slate-400 mt-1">Accepted: JPG, PNG, WebP — Max 2MB</p>
                {thumbnail && <button className="text-xs text-rose-500 mt-1" onClick={() => setThumbnail("")}>Remove image</button>}
              </div>
            </div>
          </div>

          <div>
            <Label>Content <span className="text-rose-500">*</span></Label>
            <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex flex-wrap items-center gap-0.5 bg-slate-50 border-b border-slate-200 px-2 py-1.5" data-testid="te-toolbar">
                {/* Styles */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" title="Styles" onMouseDown={(e) => e.preventDefault()} className="h-8 px-1.5 rounded flex items-center gap-1 text-slate-600 hover:bg-slate-200" data-testid="te-tool-styles"><Wand2 size={16} /><ChevronDown size={12} /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-44 p-1" align="start">
                    {STYLES.map((s) => (
                      <button key={s.block} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setBlock(s.block)} className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 ${s.className}`} data-testid={`te-style-${s.block}`}>{s.label}</button>
                    ))}
                  </PopoverContent>
                </Popover>
                <span className="w-px h-5 bg-slate-300 mx-1" />

                <TBtn title="Bold" onClick={() => run("bold")} testid="te-tool-bold"><Bold size={16} /></TBtn>
                <TBtn title="Underline" onClick={() => run("underline")} testid="te-tool-underline"><UnderlineIcon size={16} /></TBtn>
                <TBtn title="Strikethrough" onClick={() => run("strikeThrough")} testid="te-tool-strikeThrough"><Strikethrough size={16} /></TBtn>
                <TBtn title="Clear formatting" onClick={() => run("removeFormat")} testid="te-tool-removeFormat"><Eraser size={16} /></TBtn>
                <span className="w-px h-5 bg-slate-300 mx-1" />

                {/* Font family */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" title="Font family" onMouseDown={(e) => e.preventDefault()} className="h-8 px-2 rounded flex items-center gap-1 text-slate-600 hover:bg-slate-200 text-sm" data-testid="te-tool-font"><Type size={15} /><ChevronDown size={12} /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-52 p-1 max-h-72 overflow-y-auto" align="start">
                    {FONTS.map((f) => (
                      <button key={f} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setFont(f)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm" style={{ fontFamily: f }} data-testid={`te-font-${f.replace(/\s+/g, "-")}`}>{f}</button>
                    ))}
                  </PopoverContent>
                </Popover>

                {/* Font size */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" title="Font size" onMouseDown={(e) => e.preventDefault()} className="h-8 px-2 rounded flex items-center gap-1 text-slate-600 hover:bg-slate-200 text-sm font-semibold" data-testid="te-tool-size">Size<ChevronDown size={12} /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-24 p-1 max-h-72 overflow-y-auto" align="start">
                    {SIZES.map((s) => (
                      <button key={s} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setSize(s)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm" data-testid={`te-size-${s}`}>{s} pt</button>
                    ))}
                  </PopoverContent>
                </Popover>
                <span className="w-px h-5 bg-slate-300 mx-1" />

                {/* Text color */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" title="Text color" onMouseDown={(e) => e.preventDefault()} className="h-8 px-1.5 rounded flex items-center gap-0.5 text-slate-600 hover:bg-slate-200" data-testid="te-tool-color"><Baseline size={16} /><ChevronDown size={12} /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2" align="start">
                    <div className="grid grid-cols-7 gap-1.5 mb-2">
                      {SWATCHES.map((c) => (
                        <button key={c} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setColor(c)} className="w-6 h-6 rounded border border-slate-200" style={{ background: c }} data-testid={`te-color-${c}`} />
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-600">Custom<input type="color" onMouseDown={(e) => e.stopPropagation()} onChange={(e) => setColor(e.target.value)} className="h-7 w-10 rounded cursor-pointer" data-testid="te-color-custom" /></label>
                  </PopoverContent>
                </Popover>

                {/* Highlight color */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" title="Highlight color" onMouseDown={(e) => e.preventDefault()} className="h-8 px-1.5 rounded flex items-center gap-0.5 text-slate-600 hover:bg-slate-200" data-testid="te-tool-highlight"><Highlighter size={16} /><ChevronDown size={12} /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2" align="start">
                    <div className="grid grid-cols-7 gap-1.5 mb-2">
                      {["#fef08a", "#fde68a", "#bbf7d0", "#bfdbfe", "#ddd6fe", "#fbcfe8", "#fecaca"].map((c) => (
                        <button key={c} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setHighlight(c)} className="w-6 h-6 rounded border border-slate-200" style={{ background: c }} data-testid={`te-hl-${c}`} />
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-600">Custom<input type="color" onMouseDown={(e) => e.stopPropagation()} onChange={(e) => setHighlight(e.target.value)} className="h-7 w-10 rounded cursor-pointer" data-testid="te-hl-custom" /></label>
                  </PopoverContent>
                </Popover>
                <span className="w-px h-5 bg-slate-300 mx-1" />

                <TBtn title="Bullet list" onClick={() => run("insertUnorderedList")} testid="te-tool-insertUnorderedList"><List size={16} /></TBtn>
                <TBtn title="Numbered list" onClick={() => run("insertOrderedList")} testid="te-tool-insertOrderedList"><ListOrdered size={16} /></TBtn>

                {/* Alignment */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" title="Alignment" onMouseDown={(e) => e.preventDefault()} className="h-8 px-1.5 rounded flex items-center gap-0.5 text-slate-600 hover:bg-slate-200" data-testid="te-tool-align"><AlignLeft size={16} /><ChevronDown size={12} /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-1 flex gap-0.5" align="start">
                    {ALIGN_TOOLS.map((a) => (
                      <button key={a.cmd} type="button" title={a.title} onMouseDown={(e) => e.preventDefault()} onClick={() => run(a.cmd)} className="w-8 h-8 rounded flex items-center justify-center text-slate-600 hover:bg-slate-100" data-testid={`te-tool-${a.cmd}`}><a.icon size={16} /></button>
                    ))}
                  </PopoverContent>
                </Popover>

                {/* Table */}
                <Popover onOpenChange={(o) => !o && setGrid({ r: 0, c: 0 })}>
                  <PopoverTrigger asChild>
                    <button type="button" title="Insert table" onMouseDown={(e) => e.preventDefault()} className="h-8 px-1.5 rounded flex items-center gap-0.5 text-slate-600 hover:bg-slate-200" data-testid="te-tool-table"><Table2 size={16} /><ChevronDown size={12} /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3" align="start">
                    <div className="text-xs text-slate-500 mb-2 text-center" data-testid="te-table-label">{grid.r && grid.c ? `${grid.r} × ${grid.c}` : "Select size"}</div>
                    <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: "repeat(10, 18px)" }} onMouseLeave={() => setGrid({ r: 0, c: 0 })}>
                      {Array.from({ length: 8 }).map((_, ri) =>
                        Array.from({ length: 10 }).map((_, ci) => (
                          <button key={`${ri}-${ci}`} type="button" onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setGrid({ r: ri + 1, c: ci + 1 })} onClick={() => insertTable(ri + 1, ci + 1)}
                            className={`w-[18px] h-[18px] rounded-sm border ${ri < grid.r && ci < grid.c ? "bg-indigo-400 border-indigo-500" : "bg-slate-50 border-slate-200"}`} data-testid={`te-table-cell-${ri + 1}-${ci + 1}`} />
                        ))
                      )}
                    </div>
                    <div className="flex gap-1.5 mt-3 pt-2 border-t border-slate-100">
                      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={toggleTableBorders} className="flex-1 text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="te-table-borders">Show/Hide border</button>
                      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={mergeCells} className="flex-1 text-xs rounded-md border border-slate-200 px-2 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="te-table-merge">Merge cells</button>
                    </div>
                  </PopoverContent>
                </Popover>

                <TBtn title="Insert link" onClick={insertLink} testid="te-tool-link"><Link2 size={16} /></TBtn>

                {/* Image */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" title="Insert image" onMouseDown={(e) => e.preventDefault()} className="h-8 px-1.5 rounded flex items-center gap-0.5 text-slate-600 hover:bg-slate-200" data-testid="te-tool-image"><ImageIcon size={16} /><ChevronDown size={12} /></button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3 space-y-3" align="start">
                    <div>
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-1"><LinkIcon size={12} /> From URL</div>
                      <div className="flex gap-1.5">
                        <Input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="https://..." className="h-8 text-sm" data-testid="te-img-url" />
                        <Button size="sm" className="h-8" onMouseDown={(e) => e.preventDefault()} onClick={insertImageUrl} data-testid="te-img-url-add">Add</Button>
                      </div>
                    </div>
                    <div className="border-t border-slate-100 pt-2">
                      <label className="flex items-center justify-center gap-1.5 h-9 rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-500 cursor-pointer hover:border-indigo-400" data-testid="te-img-file-drop">
                        <UploadCloud size={15} /> Upload from file
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => insertImageFile(e.target.files?.[0])} data-testid="te-img-file" />
                      </label>
                    </div>
                  </PopoverContent>
                </Popover>

                <span className="w-px h-5 bg-slate-300 mx-1" />
                <button type="button" title="HTML source" onMouseDown={(e) => e.preventDefault()} onClick={() => { if (!showHtml && editorRef.current) setContent(editorRef.current.innerHTML); setShowHtml((s) => !s); }} className={`w-8 h-8 rounded flex items-center justify-center hover:bg-slate-200 ${showHtml ? "bg-slate-200 text-indigo-600" : "text-slate-600"}`} data-testid="te-tool-html"><Code2 size={16} /></button>
              </div>
              {showHtml ? (
                <textarea value={content} onChange={(e) => { setContent(e.target.value); if (editorRef.current) editorRef.current.innerHTML = e.target.value; }} className="w-full min-h-[360px] p-4 font-mono text-sm outline-none resize-y" data-testid="te-html" />
              ) : (
                <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={onEditorInput} onKeyUp={snapshot} onMouseUp={snapshot}
                  className="min-h-[360px] p-4 outline-none prose max-w-none text-sm [&_table]:border-collapse [&_td]:border [&_td]:border-slate-300 [&_td]:p-1.5" data-testid="te-content" />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => nav("/templates")} data-testid="te-cancel">Cancel</Button>
            <Button variant="outline" onClick={openPreview} className="gap-1.5" data-testid="te-preview"><Eye size={16} />Live Preview</Button>
            <Button onClick={save} disabled={saving} className="gap-1.5 bg-orange-500 hover:bg-orange-600" data-testid="te-save"><Save size={16} />{saving ? "Saving..." : "Save Template"}</Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 h-fit lg:sticky lg:top-4" data-testid="placeholders-panel">
          <div className="flex items-center gap-2 mb-3">
            <Code className="text-slate-700" size={18} />
            <h3 className="font-heading font-bold text-slate-900">Available Placeholders</h3>
          </div>
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search placeholders... e.g. father, photo, class" className="pl-9 h-9" data-testid="placeholder-search" />
          </div>
          <div className="max-h-[560px] overflow-y-auto pr-1 space-y-4">
            {filteredGroups.map((g) => {
              const Ico = ICONS[g.icon] || Code;
              return (
              <div key={g.title}>
                <div className={`flex items-center gap-1.5 font-semibold text-sm mb-2 ${g.color}`}><Ico size={15} />{g.title}</div>
                <div className="flex flex-wrap gap-1.5">
                  {g.tags.map((t) => (
                    <button key={t} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertTag(t)} className="text-xs font-mono rounded bg-slate-800 text-slate-100 px-2 py-1 hover:bg-indigo-600 transition-colors" data-testid={`ph-${t}`}>[{t}]</button>
                  ))}
                </div>
              </div>
              );
            })}
            {filteredGroups.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No placeholders match.</p>}
          </div>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="te-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye size={18} /> Live Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="shrink-0">Fill with student</Label>
            <Select value={selStudent} onValueChange={(v) => { setSelStudent(v); runPreview(v); }}>
              <SelectTrigger className="w-80" data-testid="te-preview-student"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sample">Sample data (demo student)</SelectItem>
                {students.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name} — {s.class_name || "—"}{s.school_name ? ` · ${s.school_name}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {previewLoading && <Loader2 className="animate-spin text-indigo-500" size={18} />}
            <span className="text-xs text-slate-400">Placeholders are filled with real data. Use your browser's Print to save as PDF.</span>
          </div>
          <div className="flex-1 overflow-auto bg-slate-100 p-4 rounded-lg">
            <div className="bg-white mx-auto shadow p-10 prose max-w-none text-sm [&_table]:border-collapse [&_td]:p-1.5" style={{ width: "210mm", minHeight: "280mm", boxSizing: "border-box" }} data-testid="te-preview-render" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
